import { RenderResult } from '@dojo/framework/core/interfaces';
import { focus } from '@dojo/framework/core/middleware/focus';
import { i18n } from '@dojo/framework/core/middleware/i18n';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { FocusProperties } from '@dojo/framework/core/mixins/Focus';
import { create, v, w } from '@dojo/framework/core/vdom';
import { Keys, formatAriaProperties, isRenderResult } from '@dojo/widgets/common/util';
import { Icon } from '@dojo/widgets/icon';
import theme, { ThemeProperties } from '@dojo/widgets/middleware/theme';
import { TextInput } from '@dojo/widgets/text-input';
import * as css from '@dojo/widgets/theme/default/date-input.m.css';
import * as textInputCss from '@dojo/widgets/theme/default/text-input.m.css';

import { InlineTriggerPopup } from '../middleware/inlineTriggerPopup';
import localCss from './css/date-input.m.css';
import { formatDate, formatDateISO, parseDate } from './date-utils';
import { Calendar } from './dojo-calendar';
import bundle from './nls/DateInput';

export interface DateInputProperties extends ThemeProperties, FocusProperties {
	/** The initial value */
	initialValue?: string;
	/** Controlled value property */
	value?: string;
	/** Set the latest date the calendar will display in (it will show the whole month but not allow previous selections) */
	max?: string;
	/** Set the earliest date the calendar will display (it will show the whole month but not allow previous selections) */
	min?: string;
	/** name used on the underlying form input's name attribute */
	name?: string;
	/** Callback fired with new value in YYYY-MM-DD format */
	onValue?(date: string): void;
	/** Callback fired when input validation changes */
	onValidate?: (valid: boolean | undefined, message: string) => void;
	/** The disabled property of the input */
	disabled?: boolean;
	/** The readonly attribute of the input */
	readOnly?: boolean;
	/** The label used for icon button (HC-specific) */
	iconLabel?: string;
}

export interface DateInputChildren {
	/** The label for the wrapped text input */
	label?: RenderResult;
}

interface DateInputICache {
	/** The most recent "initialValue" property passed */
	initialValue: string;
	/** Current user-inputted value */
	inputValue: string;
	/** The last valid Date of value */
	value: Date;
	/** The last "value" property passed */
	lastValue: string;
	/** A possible new value that should not be saved until we call a callback */
	nextValue: string;
	/** Should validate the input value on the next cycle */
	shouldValidate: boolean;
	/** Message for current validation state */
	validationMessage: string | undefined;
	/** Indicates which node will be focused */
	focusNode: 'input' | 'calendar';
	/** Current validity state of the input */
	isValid: boolean | undefined;
	/** Whether the calendar popup is currently open */
	isOpen: boolean;
}

const icache = createICacheMiddleware<DateInputICache>();
const factory = create({ theme, icache, i18n, focus })
	.properties<DateInputProperties>()
	.children<DateInputChildren | RenderResult | undefined>();

export const DateInput = factory(function ({ id, properties, children, middleware: { theme, icache, i18n, focus } }) {
	const {
		initialValue,
		name,
		onValue,
		onValidate,
		value: controlledValue,
		iconLabel = ''
		// disabled = false,
		// readOnly = false
	} = properties();

	const { messages } = i18n.localize(bundle);
	const classes = theme.classes(css);
	const max = parseDate(properties().max);
	const min = parseDate(properties().min);

	if (initialValue !== undefined && controlledValue === undefined && icache.get('initialValue') !== initialValue) {
		const parsed = initialValue && parseDate(initialValue);

		if (parsed) {
			icache.set('inputValue', formatDate(parsed));
		}
		icache.set('initialValue', initialValue);
		icache.set('shouldValidate', true);
	}

	if (controlledValue !== undefined && icache.get('lastValue') !== controlledValue) {
		const parsed = controlledValue && parseDate(controlledValue);
		if (parsed) {
			icache.set('inputValue', formatDate(parsed));
			icache.set('value', parsed);
		}
		icache.set('lastValue', controlledValue);
	}

	const inputValue = icache.getOrSet('inputValue', () => {
		const parsed = initialValue && parseDate(initialValue);

		return formatDate(parsed || new Date());
	});
	const validationId = `date-input-validation-${id}`;
	const shouldValidate = icache.getOrSet('shouldValidate', true);
	const isOpen = icache.getOrSet('isOpen', false);
	const shouldFocus = focus.shouldFocus();
	const focusNode = icache.getOrSet('focusNode', 'input');
	const [labelChild] = children();
	const label = isRenderResult(labelChild) ? labelChild : labelChild.label;

	if (shouldValidate) {
		const testValue = icache.get('nextValue') || inputValue;
		let isValid: boolean | undefined;
		const validationMessages: string[] = [];

		// if min & max create an impossible range, no need to validate anything else
		if (min && max && min > max) {
			validationMessages.push(messages.invalidProps);
			isValid = false;
		} else {
			const newDate = parseDate(testValue);

			if (newDate !== undefined) {
				if (min && newDate < min) {
					validationMessages.push(messages.tooEarly);
				} else if (max && newDate > max) {
					validationMessages.push(messages.tooLate);
				} else {
					if (controlledValue === undefined) {
						icache.set('value', newDate);
						icache.set('inputValue', formatDate(newDate));
					}
					if (onValue) {
						onValue(formatDateISO(newDate));
					}
				}
			} else {
				validationMessages.push(messages.invalidDate);
			}

			isValid = validationMessages.length === 0;
		}

		const validationMessage = validationMessages.join('; ');
		onValidate && onValidate(isValid, validationMessage);
		icache.set('validationMessage', validationMessage);
		icache.set('isValid', isValid);
		icache.set('shouldValidate', false);
	}

	return v('div', { widgetId: id, classes: [theme.variant(), classes.root] }, [
		v('input', { type: 'hidden', name: name, value: formatDateISO(icache.get('value')), 'aria-hidden': 'true' }),
		w(InlineTriggerPopup, { key: 'popup' }, [
			{
				trigger: (toggleOpen) => {
					function openCalendar() {
						icache.set('focusNode', 'calendar');
						icache.set('isOpen', true);
						focus.focus();
						toggleOpen();
					}
					return v('div', { classes: classes.input }, [
						w(
							TextInput,
							{
								key: 'input',
								focus: () => shouldFocus && focusNode === 'input',
								theme: theme.compose(textInputCss, css, 'input'),
								type: 'text',
								initialValue: icache.get('inputValue'),
								onBlur: () => icache.set('shouldValidate', true),
								onValue: (v) => {
									icache.set(controlledValue === undefined ? 'inputValue' : 'nextValue', v || '');
								},
								onKeyDown: (key, preventDefault) => {
									if (key === Keys.Down || key === Keys.Space || key === Keys.Enter) {
										preventDefault();
										openCalendar();
									}
								},
								aria: {
									describedby: validationId,
									invalid: icache.get('isValid') === false ? 'true' : null
								}
							},
							[
								{
									label,
									trailing: v(
										'button',
										{
											key: 'dateIcon',
											onclick: (e) => {
												e.stopPropagation();
												openCalendar();
											},
											classes: classes.toggleCalendarButton,
											type: 'button',
											'aria-expanded': isOpen ? 'true' : 'false',
											'aria-haspopup': 'dialog',
											'aria-controls': `date-input-calendar-${id}`,
											...formatAriaProperties({ label: iconLabel || messages.calendar })
										},
										[w(Icon, { type: 'dateIcon' })]
									)
								}
							]
						),
						v(
							'p',
							{
								classes: icache.get('validationMessage') ? localCss.invalid : undefined,
								id: validationId,
								'aria-live': 'polite'
							},
							[
								icache.get('validationMessage')
									? v('span', { 'aria-hidden': 'true' }, ['\u26a0 '])
									: null,
								icache.get('validationMessage') || ''
							]
						)
					]);
				},
				content: (onClose) => {
					function closeCalendar() {
						icache.set('focusNode', 'input');
						icache.set('isOpen', false);
						focus.focus();
						onClose();
					}
					return v(
						'div',
						{
							classes: [classes.popup, localCss.calendar],
							id: `date-input-calendar-${id}`,
							role: 'dialog',
							'aria-label': messages.calendar
						},
						[
							w(Calendar, {
								key: 'calendar',
								focus: () => shouldFocus && focusNode === 'calendar',
								maxDate: max,
								minDate: min,
								initialValue: icache.get('value'),
								onValue: (date) => {
									icache.set(
										controlledValue === undefined ? 'inputValue' : 'nextValue',
										formatDate(date)
									);
									icache.set('shouldValidate', true);
									closeCalendar();
								}
							})
						]
					);
				}
			}
		])
	]);
});

export default DateInput;
